import React, { useMemo, useState } from 'react';
import { Badge, Button, Empty, Popover, Spin, Tooltip, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useBuoc6ChiTieuWarningHeader } from '@/hooks/useBuoc6ChiTieuWarningHeader';
import { usePrivateStore } from '@/store/usePrivateStore';
import { ROUTES } from '@/utils/pathRoutes';

interface Props {
  enabled: boolean;
}

export const Buoc6ChiTieuWarningHeaderNotify: React.FC<Props> = ({ enabled }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const { activeWarnings, activeCount, loading, isBuoc6Page, isChiTieuChaConPage, featureEnabled } =
    useBuoc6ChiTieuWarningHeader(enabled);
  const departmentLabel = usePrivateStore((s) => s.treeSelectDepartment.selectedValue?.label || '');

  const popoverContent = useMemo(
    () => (
      <div style={{ width: 320, maxHeight: 360, overflow: 'auto' }}>
        {departmentLabel ? (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginBottom: 8, lineHeight: 1.4 }}
          >
            {t('bsc.label.phong_ban_dang_xem', 'Phòng ban đang xem')}:{' '}
            <Typography.Text style={{ fontSize: 12 }}>{departmentLabel}</Typography.Text>
          </Typography.Text>
        ) : null}
        {loading && activeCount === 0 ? (
          <div className="flex justify-center py-6">
            <Spin size="small" />
          </div>
        ) : activeCount === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('bsc.msg.khong_co_canh_bao_chitieu', 'Không có cảnh báo chỉ tiêu')}
          />
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {activeWarnings.map((w) => (
              <li
                key={w.kpiId}
                style={{
                  padding: '8px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Typography.Text strong style={{ fontSize: 13 }}>
                  {w.kpiName}
                </Typography.Text>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{w.mucTieuName}</div>
              </li>
            ))}
          </ul>
        )}
        {!isChiTieuChaConPage && activeCount > 0 && (
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Button
              type="link"
              size="small"
              onClick={() => void router.push(ROUTES.BUOC6_CHI_TIEU_CHA_CON)}
            >
              {t('bsc.btn.xemchitiet', 'Xem chi tiết')}
            </Button>
            {!isBuoc6Page && (
              <Button type="link" size="small" onClick={() => void router.push(ROUTES.BUOC6)}>
                {t('bsc.btn.mo_buoc6', 'Mở Bước 6')}
              </Button>
            )}
          </div>
        )}
      </div>
    ),
    [activeCount, activeWarnings, departmentLabel, isBuoc6Page, isChiTieuChaConPage, loading, router, t]
  );

  // Chỉ hiện trên phân hệ BSC
  if (!featureEnabled || (!loading && activeCount === 0)) {
    return null;
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      title={
        departmentLabel
          ? `${t('bsc.msg.canh_bao_chitieu_cha_con', 'Cảnh báo chỉ tiêu cha/con')} — ${departmentLabel}`
          : t('bsc.msg.canh_bao_chitieu_cha_con', 'Cảnh báo chỉ tiêu cha/con')
      }
      content={popoverContent}
    >
      <Tooltip
        title={t(
          'bsc.msg.chitieu_con_khong_khop_header',
          departmentLabel
            ? '{{count}} KPI chưa khớp ({{department}})'
            : 'Có {{count}} KPI chỉ tiêu cha/con chưa khớp',
          { count: activeCount, department: departmentLabel }
        )}
        placement="bottom"
      >
        <div className="relative cursor-pointer transition-transform duration-200 hover:scale-110 hover:-translate-y-0.5">
          <Badge count={activeCount} size="small" offset={[-2, 2]}>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF7E6] text-[#FAAD14]">
              <WarningOutlined style={{ fontSize: 18 }} />
            </div>
          </Badge>
        </div>
      </Tooltip>
    </Popover>
  );
};
